import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';  // Certifique-se de que jwt-decode está sendo importado corretamente
import { authBaseUrl } from '../config/runtime';

const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState(null);
  const [role, setRole] = useState(null);
  const [userName, setUserName] = useState(null);

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('role');
    localStorage.removeItem('attendantName');
    localStorage.removeItem('companyCode');
    localStorage.removeItem('department');
    localStorage.removeItem('activeArea');
    localStorage.removeItem('availableAreas');
    localStorage.removeItem('pendingAreaSelection');
    setIsAuthenticated(false);
    setUserId(null);
    setRole(null);
    setUserName(null);
  }, []);

  const applyIdentity = useCallback((identity) => {
    const newUserId = identity.email || identity.username || String(identity.id || '');
    const newRole = identity.role;
    const newCompanyCode = identity.company_code;
    const newName = identity.name || identity.email || identity.username;
    const newDepartment = identity.department || 'operacional';
    const fallbackActiveArea = identity.active_area || ((newRole || '').toLowerCase() === 'admin' || (newRole || '').toLowerCase() === 'master' ? 'full' : newDepartment);
    const storedActiveArea = localStorage.getItem('activeArea');
    const newActiveArea = storedActiveArea || fallbackActiveArea;
    setIsAuthenticated(true);
    setUserId(newUserId);
    setRole(newRole || null);
    setUserName(newName || null);
    localStorage.setItem('userId', newUserId);
    if (newRole) {
      localStorage.setItem('role', newRole);
    }
    if (newName) {
      localStorage.setItem('attendantName', newName);
    }
    if (newCompanyCode) {
      localStorage.setItem('companyCode', newCompanyCode);
    }
    localStorage.setItem('department', newDepartment);
    localStorage.setItem('activeArea', newActiveArea);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      clearSession();
      return undefined;
    }

    let decoded;
    try {
      decoded = jwtDecode(token);
    } catch (error) {
      clearSession();
      return undefined;
    }

    const exp = Number(decoded?.exp || 0);
    if (exp && Date.now() >= exp * 1000) {
      clearSession();
      return undefined;
    }

    const identity = decoded?.sub || {};
    applyIdentity(identity);

    let cancelled = false;
    let heartbeatTimer = null;

    const validateToken = async () => {
      try {
        await axios.get(`${authBaseUrl}/me`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000
        });
      } catch (error) {
        if (!cancelled) {
          clearSession();
        }
      }
    };
    validateToken();

    const heartbeat = async () => {
      try {
        await axios.post(`${authBaseUrl}/presence/heartbeat`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000
        });
      } catch (error) {
        // noop: a validação do token já trata sessão inválida
      }
    };
    heartbeat();
    heartbeatTimer = setInterval(heartbeat, 30000);

    let expirationTimer = null;
    if (exp) {
      const msUntilExpiration = Math.max(exp * 1000 - Date.now(), 0);
      expirationTimer = setTimeout(() => {
        if (!cancelled) {
          clearSession();
        }
      }, msUntilExpiration);
    }

    return () => {
      cancelled = true;
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
      }
      if (expirationTimer) {
        clearTimeout(expirationTimer);
      }
    };
  }, [applyIdentity, clearSession]);

  const handleLogout = useCallback(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.post(`${authBaseUrl}/presence/offline`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 3000
      }).catch(() => {});
    }
    clearSession();
  }, [clearSession]);

  return { isAuthenticated, setIsAuthenticated, handleLogout, userId, role, userName };
};

export default useAuth;
